import { useState } from 'react'
import { COST_ACTIVITY, COST_TYPE, type CostActivity, type CostEntryRecord, type CostType } from '@henlytics/types'
import { Money } from '../components/common/Money'
import { DataTable, type Column } from '../components/common/DataTable'
import { EmptyState } from '../components/common/EmptyState'
import { DateCell } from '../components/entry/DateCell'
import { EntryGrid } from '../components/entry/EntryGrid'
import { NumericCell } from '../components/entry/NumericCell'
import { useBatches } from '../hooks/useBatches'
import { useCostEntries, useCreateCostEntry, useVoidCostEntry } from '../hooks/useCosting'
import { today } from '../hooks/useReports'
import { clientUuid } from '../lib/uuid'

const SHARED = '__shared__'

export function Costs() {
  const batches = useBatches()
  const entries = useCostEntries()
  const create = useCreateCostEntry()
  const voidEntry = useVoidCostEntry()

  const [costDate, setCostDate] = useState(today())
  const [target, setTarget] = useState<string>(SHARED)
  const [costType, setCostType] = useState<CostType>('labor')
  const [activity, setActivity] = useState<CostActivity>('feeding')
  const [amount, setAmount] = useState('')
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [notes, setNotes] = useState('')
  const [entryId, setEntryId] = useState(() => clientUuid())

  const isShared = target === SHARED

  const columns: ReadonlyArray<Column<CostEntryRecord>> = [
    { key: 'date', header: 'Date', render: (r) => r.costDate },
    {
      key: 'batch',
      header: 'Batch',
      render: (r) =>
        r.batchId ? (batches.data?.find((b) => b.id === r.batchId)?.code ?? r.batchId) : <em>shared</em>,
    },
    { key: 'type', header: 'Type', render: (r) => r.costType },
    { key: 'activity', header: 'Activity', render: (r) => r.activity },
    { key: 'amount', header: 'Amount', numeric: true, render: (r) => <Money value={r.amount} /> },
    { key: 'period', header: 'Period', render: (r) => (r.periodStart === r.periodEnd ? '—' : `${r.periodStart} → ${r.periodEnd}`) },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.voidedAt ? (
          <span className="muted">voided</span>
        ) : (
          <button type="button" className="link-button" onClick={() => voidEntry.mutate(r.id)} disabled={voidEntry.isPending}>
            Void
          </button>
        ),
    },
  ]

  return (
    <div className="page">
      <header className="page-head">
        <h2>Costs</h2>
        <p className="muted">
          A shared cost is allocated across batches by bird-days — never evenly, never by headcount (§3.7).
          Until Phase 2 wires purchases to the weighted average, feed cost is entered here with activity
          &ldquo;feeding&rdquo;.
        </p>
      </header>

      <EntryGrid
        title="New cost entry"
        onSubmit={() =>
          create.mutate(
            {
              batchId: isShared ? null : target,
              costDate,
              costType,
              activity,
              amount,
              periodStart: isShared && periodStart ? periodStart : null,
              periodEnd: isShared && periodEnd ? periodEnd : null,
              notes: notes.trim() || null,
              clientUuid: entryId,
            },
            {
              onSuccess: () => {
                setAmount('')
                setNotes('')
                setEntryId(clientUuid())
              },
            },
          )
        }
        submitLabel="Record cost"
        busy={create.isPending}
        error={create.isError ? create.error.message : null}
        success={create.isSuccess ? 'Recorded.' : null}
      >
        <DateCell label="Date" value={costDate} onChange={setCostDate} />
        <label className="cell">
          <span className="cell-label">Charge to</span>
          <select className="cell-input" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value={SHARED}>Shared — allocate by bird-days</option>
            {(batches.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.code}
              </option>
            ))}
          </select>
        </label>
        <label className="cell">
          <span className="cell-label">Cost type</span>
          <select className="cell-input" value={costType} onChange={(e) => setCostType(e.target.value as CostType)}>
            {COST_TYPE.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <label className="cell">
          <span className="cell-label">Activity</span>
          <select className="cell-input" value={activity} onChange={(e) => setActivity(e.target.value as CostActivity)}>
            {COST_ACTIVITY.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <NumericCell label="Amount" value={amount} onChange={setAmount} />
        {isShared ? (
          <>
            <DateCell label="Period start" value={periodStart || costDate} onChange={setPeriodStart} />
            <DateCell label="Period end" value={periodEnd || costDate} onChange={setPeriodEnd} />
          </>
        ) : null}
        <label className="cell wide">
          <span className="cell-label">Notes</span>
          <input className="cell-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </EntryGrid>

      <section>
        <h3>Recorded costs</h3>
        <DataTable
          rows={entries.data ?? []}
          columns={columns}
          rowKey={(r) => r.id}
          rowClassName={(r) => (r.voidedAt ? 'voided' : undefined)}
          empty={<EmptyState title="No costs recorded yet." />}
        />
      </section>
    </div>
  )
}
